import { isPermissionOfEntity, PERMISSIONS_BY_ENTITY, type PermissionOf, type PermissionSetEntity } from './permissionSetEntities'

/** The two Omnea-defined fixed sets an organisation cannot edit, as the UI names them. */
export const GRANT_FIXED_LEVELS = ['viewer', 'owner'] as const

export type GrantFixedLevel = (typeof GRANT_FIXED_LEVELS)[number]

// A fixed set is expanded by the model rather than stored as tuples, so what it confers cannot be
// read back from the store — it is duplicated here for anything that displays it.
const FIXED_SET_VIEWER_PERMISSIONS = {
  request: ['view', 'create', 'view_documents'],
  supplier: [
    'view',
    'view_tprm',
    'view_related_requests',
    'view_related_agreements',
    'view_related_documents',
    'view_related_submissions',
  ],
  agreement: ['view', 'view_related_supplier', 'view_related_requests', 'view_documents'],
  bill: [],
  workflow: [],
} as const satisfies { [Entity in PermissionSetEntity]: readonly PermissionOf<Entity>[] }

/**
 * Whether an Omnea-defined set confers one permission of the entity's vocabulary. Owner subsumes Viewer.
 *
 * A permission of another entity is conferred by neither level. Callers holding a runtime entity get
 * the whole vocabulary in `permission`, so without the check Owner would claim to confer permissions
 * its vocabulary does not have.
 */
export const fixedSetGrants = <Entity extends PermissionSetEntity>(
  entity: Entity,
  level: GrantFixedLevel,
  permission: PermissionOf<Entity>
): boolean => {
  if (!isPermissionOfEntity(entity, permission)) return false
  const viewerPermissions: readonly string[] = FIXED_SET_VIEWER_PERMISSIONS[entity]
  return level === 'owner' || viewerPermissions.includes(permission)
}

/** The lowest Omnea-defined set that confers anything on the entity, since Viewer confers nothing on some. */
export const lowestConferringFixedLevel = (entity: PermissionSetEntity): GrantFixedLevel => {
  const vocabulary: readonly PermissionOf<PermissionSetEntity>[] = PERMISSIONS_BY_ENTITY[entity]
  return GRANT_FIXED_LEVELS.find((level) => vocabulary.some((permission) => fixedSetGrants(entity, level, permission))) ?? 'owner'
}
